import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized } from "@lit/localize";

import { blockStyles } from "../../../common/styles/block-styles.js";
import { navigateOnClick } from "../../../common/router/app-navigation.js";

/** Ein Eintrag der Verweiszeile: gesetzt (ohne Pfad) oder Link (mit Pfad). */
export interface Verweis {
  /** Schlüssel, etwa der `art`-Slug — für den Key im Template. */
  schluessel: string;
  beschriftung: string;
  /** Ohne Pfad ist der Eintrag GESETZT: Er ist schon zu sehen. */
  pfad?: string;
}

/** Ein Fußlink unter der Zeile — „Alle Wahlen", „Kommunalwahl 2020 ansehen". */
export interface Fusslink {
  text: string;
  pfad: string;
}

/**
 * Die Verweiszeile — welche Arten es gibt, welche gerade zu sehen ist, und wo
 * die anderen stehen.
 *
 * Ein VERWEIS, kein Register (Fachkonzept 1.4): Ein Register wäre ein
 * Bedienelement und machte den Block zum Werkzeug; die Zeile sagt nur „es
 * gab an dem Tag noch drei andere Wahlen, und die liegen dort". Die gezeigte
 * Art steht gesetzt da — ein Link auf das, was man schon sieht, wäre eine
 * Sackgasse.
 *
 * Das Bauteil entscheidet NICHT, welche Arten fehlen oder wohin ein Verweis
 * führt: Einträge und Pfade kommen fertig herein (`wahl-auswahl.ts`).
 * Darunter, leiser, die Fußlinks — der Weg ins Kapitel und, auf den
 * Gebietsseiten, der Weg zur vorigen Wahl.
 */
@customElement("app-wahl-verweiszeile")
@localized()
export class AppWahlVerweiszeile extends LitElement {
  @property({ attribute: false }) eintraege: Verweis[] = [];

  @property({ attribute: false }) fusslinks: Fusslink[] = [];

  static override styles = [
    blockStyles,
    css`
      :host {
        padding: 0;
        max-width: none;
      }
      ul {
        display: flex;
        flex-wrap: wrap;
        gap: var(--portal-space-1) var(--portal-space-4);
        margin: 0;
        padding: 0;
        list-style: none;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
      }
      a {
        color: var(--portal-ink-2);
        text-decoration: underline;
      }
      a:hover {
        color: var(--portal-akzent);
      }
      /* Die gezeigte Art: gesetzt, nicht anklickbar. */
      .gesetzt {
        color: var(--portal-ink);
        font-weight: 500;
      }
      .fuss {
        display: flex;
        flex-wrap: wrap;
        gap: var(--portal-space-1) var(--portal-space-6);
        margin-top: var(--portal-space-3);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-sm);
      }
      .fuss a {
        text-decoration: none;
      }
    `,
  ];

  override render() {
    // Unter zwei Einträgen gibt es nichts zu verweisen — dann nur die Fußlinks.
    const zeile =
      this.eintraege.length >= 2
        ? html`<ul>
            ${this.eintraege.map((e) =>
              e.pfad
                ? html`<li>
                    <a href=${e.pfad} @click=${(ev: Event) => navigateOnClick(ev, e.pfad!)}
                      >${e.beschriftung} →</a
                    >
                  </li>`
                : html`<li aria-current="true" class="gesetzt">${e.beschriftung}</li>`,
            )}
          </ul>`
        : nothing;
    const fuss = this.fusslinks.length
      ? html`<p class="fuss">
          ${this.fusslinks.map(
            (f) =>
              html`<a href=${f.pfad} @click=${(ev: Event) => navigateOnClick(ev, f.pfad)}
                >${f.text} →</a
              >`,
          )}
        </p>`
      : nothing;
    if (zeile === nothing && fuss === nothing) return nothing;
    return html`${zeile}${fuss}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-wahl-verweiszeile": AppWahlVerweiszeile;
  }
}
